// Lightweight Sentry-style logger that records unexpected client errors into
// the `error_events` table. Never throws; failures are swallowed so the error
// UI is never blocked on telemetry.
import { supabase } from "@/integrations/supabase/client";

interface LogInput {
  message: string;
  stack?: string | null;
  component_stack?: string | null;
  kind?: string;
  metadata?: Record<string, unknown> | null;
}

const RECENT: string[] = [];
const DEDUPE_WINDOW = 20;

export async function logClientError(input: LogInput): Promise<void> {
  try {
    const key = `${input.kind ?? "error"}::${(input.message ?? "").slice(0, 200)}`;
    if (RECENT.includes(key)) return;
    RECENT.push(key);
    if (RECENT.length > DEDUPE_WINDOW) RECENT.shift();

    const { data: sess } = await supabase.auth.getSession();
    const user_id = sess?.session?.user?.id ?? null;

    await supabase.from("error_events").insert({
      user_id,
      kind: input.kind ?? "react_error_boundary",
      message: (input.message ?? "unknown").slice(0, 2000),
      stack: input.stack ? String(input.stack).slice(0, 8000) : null,
      component_stack: input.component_stack ? String(input.component_stack).slice(0, 8000) : null,
      route: typeof window !== "undefined" ? window.location.pathname + window.location.search : null,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
      metadata: (input.metadata ?? null) as never,
    });
  } catch {
    /* best-effort telemetry */
  }
}

let bound = false;
export function bindGlobalErrorHandlers() {
  if (bound || typeof window === "undefined") return;
  bound = true;
  window.addEventListener("error", (ev) => {
    void logClientError({
      kind: "window_error",
      message: ev.message || (ev.error?.message ?? "window error"),
      stack: ev.error?.stack ?? null,
    });
  });
  window.addEventListener("unhandledrejection", (ev) => {
    const reason = (ev as PromiseRejectionEvent).reason;
    const message = reason instanceof Error ? reason.message : String(reason ?? "unhandled rejection");
    void logClientError({
      kind: "unhandled_rejection",
      message,
      stack: reason instanceof Error ? reason.stack : null,
    });
  });
}
